import { fetchPubMedArticles } from '../PubMedUtilities'
import { enrichArticles } from './pubmedEnrichment'
import type { EnrichedPubMedArticle } from './pubmedEnrichment'
import type { PubMedArticle, PubMedSearchParams } from '../types'

const DEFAULT_CHUNK_SIZE = 50

export interface FetchAllPubMedArticlesResult {
    articles: EnrichedPubMedArticle[]
    failedChunkIds: string[]
}

/**
 * Fetch full article data for every id in `ids`, in sequential chunks of `chunkSize`
 * (default 50), and enrich (score) the accumulated set once against `params`.
 *
 * Does NOT sort - callers should call `sortEnrichedArticles(result.articles, sortMode)`
 * separately so that re-sorting on sortMode toggle never needs a refetch.
 *
 * A chunk that throws is caught and its ids recorded in `failedChunkIds`; the remaining
 * chunks still proceed instead of aborting the whole fetch.
 */
export async function fetchAndEnrichAllPubMedArticles(
    ids: string[],
    params: PubMedSearchParams,
    token: string,
    chunkSize: number = DEFAULT_CHUNK_SIZE
): Promise<FetchAllPubMedArticlesResult> {
    if (!ids.length) return { articles: [], failedChunkIds: [] }

    const allRaw: PubMedArticle[] = []
    const failedChunkIds: string[] = []

    for (let i = 0; i < ids.length; i += chunkSize) {
        const chunk = ids.slice(i, i + chunkSize)
        try {
            const rawChunk = await fetchPubMedArticles(chunk, token)
            const normalizedChunk = Array.isArray(rawChunk)
                ? rawChunk
                : Array.isArray((rawChunk as any)?.articles)
                  ? (rawChunk as any).articles
                  : Array.isArray((rawChunk as any)?.data)
                    ? (rawChunk as any).data
                    : []
            allRaw.push(...normalizedChunk)
        } catch (err) {
            console.warn('[PubMed] Failed to fetch chunk:', chunk, err)
            failedChunkIds.push(...chunk)
        }
    }

    const originalRankByPmid = new Map(ids.map((pmid, index) => [String(pmid), index]))

    const rawArticlesInPubMedOrder = [...allRaw].sort((a, b) => {
        const aRank = originalRankByPmid.get(String(a.pmid)) ?? Number.MAX_SAFE_INTEGER
        const bRank = originalRankByPmid.get(String(b.pmid)) ?? Number.MAX_SAFE_INTEGER

        return aRank - bRank
    })

    return {
        articles: enrichArticles(rawArticlesInPubMedOrder, params),
        failedChunkIds,
    }
}
